import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Upload, 
  Trash2, 
  Image as ImageIcon, 
  FileText, 
  Loader2,
  Eye,
  Palette,
  Settings,
  Save,
  Move,
  Pencil
} from 'lucide-react';
import BadgeTemplateEditor from './BadgeTemplateEditor';

// System fields that can be shown on badges
const SYSTEM_BADGE_FIELDS = [
  { key: 'name', label: 'Full Name', default: true },
  { key: 'call_name', label: 'Call Name / Nickname', default: false },
  { key: 'ticket_id', label: 'Ticket ID', default: true },
  { key: 'category_name', label: 'Category', default: true },
  { key: 'email', label: 'Email', default: false },
  { key: 'phone_number', label: 'Phone Number', default: false },
  { key: 'club_name', label: 'Club / Company', default: false },
  { key: 'gender', label: 'Gender', default: false },
  { key: 'tshirt_size', label: 'T-Shirt Size', default: false },
  { key: 'dietary_preference', label: 'Dietary Preference', default: false },
  { key: 'qr_code', label: 'QR Code', default: true }
];

const BadgeTemplateManager = ({ eventId, categories }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fieldConfigOpen, setFieldConfigOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    template_name: '',
    category_id: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Badge field configuration
  const [badgeFields, setBadgeFields] = useState({});
  const [customFieldsList, setCustomFieldsList] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/badge-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const fetchBadgeFieldConfig = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/badge-field-config`);
      setBadgeFields(response.data?.fields || getDefaultFields());
    } catch (error) {
      setBadgeFields(getDefaultFields());
    }
  }, [eventId]);

  const fetchCustomFields = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/custom-fields`);
      setCustomFieldsList(response.data || []);
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
    }
  }, [eventId]);

  useEffect(() => {
    fetchTemplates();
    fetchBadgeFieldConfig();
    fetchCustomFields();
  }, [fetchTemplates, fetchBadgeFieldConfig, fetchCustomFields]);

  const getDefaultFields = () => {
    const defaults = {};
    SYSTEM_BADGE_FIELDS.forEach(field => {
      defaults[field.key] = field.default;
    });
    return defaults;
  };

  const handleFieldToggle = (fieldKey) => {
    setBadgeFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  const handleSaveFieldConfig = async () => {
    setSavingFields(true);
    try {
      await api.put(`/events/${eventId}/badge-field-config`, { fields: badgeFields });
      toast.success('Badge field configuration saved!');
      setFieldConfigOpen(false);
    } catch (error) {
      toast.error('Failed to save field configuration');
    } finally {
      setSavingFields(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload PNG, JPG, or PDF file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadTemplate = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    if (!newTemplate.category_id) {
      toast.error('Please select a category');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('category_id', newTemplate.category_id);
    formData.append('template_name', newTemplate.template_name || `Template for ${categories.find(c => c.category_id === newTemplate.category_id)?.category_name}`);

    try {
      await api.post(`/events/${eventId}/badge-templates/upload-design`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Badge template uploaded successfully!');
      setUploadDialogOpen(false);
      setNewTemplate({ template_name: '', category_id: '' });
      setSelectedFile(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    
    try {
      await api.delete(`/events/${eventId}/badge-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleOpenEditor = (template) => {
    // Add category info to template
    const cat = categories.find(c => c.category_id === template.category_id);
    setEditingTemplate({
      ...template,
      category_name: cat?.category_name,
      category_color: cat?.category_color
    });
    setEditorOpen(true);
  };

  const handleEditorSave = (fieldMappings) => {
    setEditorOpen(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  const getFileIcon = (filename) => {
    if (filename?.endsWith('.pdf')) {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    return <ImageIcon className="h-8 w-8 text-blue-500" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" /> Badge Templates
          </CardTitle>
          <CardDescription>
            Upload custom badge designs for different attendee categories
          </CardDescription>
        </div>
        <div className="flex gap-2">
          {/* Badge Field Configuration */}
          <Dialog open={fieldConfigOpen} onOpenChange={setFieldConfigOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="configure-fields-btn">
                <Settings className="h-4 w-4 mr-2" /> Configure Fields
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Badge Field Configuration</DialogTitle>
                <DialogDescription>
                  Choose which fields to display on attendee badges
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <h4 className="font-medium text-sm mb-3">System Fields</h4>
                  <div className="space-y-3">
                    {SYSTEM_BADGE_FIELDS.map((field) => (
                      <div key={field.key} className="flex items-center space-x-3">
                        <Checkbox
                          id={`field-${field.key}`}
                          checked={badgeFields[field.key] || false}
                          onCheckedChange={() => handleFieldToggle(field.key)}
                          disabled={field.key === 'qr_code'} // QR code is always required
                        />
                        <Label 
                          htmlFor={`field-${field.key}`} 
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {field.label}
                          {field.key === 'qr_code' && (
                            <span className="text-xs text-gray-400 ml-2">(required)</span>
                          )}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                
                {customFieldsList.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-sm mb-3">Custom Fields (from Excel)</h4>
                    <div className="space-y-3">
                      {customFieldsList.map((fieldName) => (
                        <div key={fieldName} className="flex items-center space-x-3">
                          <Checkbox
                            id={`custom-${fieldName}`}
                            checked={badgeFields[`custom_${fieldName}`] || false}
                            onCheckedChange={() => handleFieldToggle(`custom_${fieldName}`)}
                          />
                          <Label 
                            htmlFor={`custom-${fieldName}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {fieldName}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {customFieldsList.length === 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                    <p>No custom fields found. Upload attendees via Excel with additional columns to see custom fields here.</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setFieldConfigOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveFieldConfig} disabled={savingFields}>
                  {savingFields ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Save Configuration</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Upload Template Dialog */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="upload-template-btn">
                <Plus className="h-4 w-4 mr-2" /> Upload Design
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Badge Design</DialogTitle>
              <DialogDescription>
                Upload a custom badge design for a specific category. Supported formats: PNG, JPG, PDF
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUploadTemplate} className="space-y-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={newTemplate.category_id}
                  onValueChange={(v) => setNewTemplate({ ...newTemplate, category_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: cat.category_color }}
                          />
                          {cat.category_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  value={newTemplate.template_name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, template_name: e.target.value })}
                  placeholder="e.g., VIP Badge Design"
                />
              </div>

              <div className="space-y-2">
                <Label>Design File *</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  {selectedFile ? (
                    <div className="flex flex-col items-center gap-2">
                      {getFileIcon(selectedFile.name)}
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedFile(null)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                      <Input
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="template-file"
                      />
                      <Label htmlFor="template-file" className="cursor-pointer">
                        <span className="text-primary hover:underline">Click to upload</span>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, or PDF (max 5MB)</p>
                      </Label>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-800 mb-1">📐 Recommended Dimensions</p>
                <p className="text-amber-700">
                  <strong>Portrait orientation (A6):</strong> 105mm × 148mm (4.1&quot; × 5.8&quot;)<br />
                  Resolution: 300 DPI for print quality<br />
                  <em>Leave space at bottom for QR code (~25mm)</em>
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading || !selectedFile}>
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload Template</>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {templates.length === 0 ? (
          <div className="text-center py-8">
            <Palette className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">No custom templates yet</p>
            <p className="text-sm text-gray-400">
              Using default badge design. Upload custom designs to personalize badges for each category.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.template_id} className="overflow-hidden" data-testid={`template-${template.template_id}`}>
                <div className="aspect-[0.7/1] bg-gray-100 flex items-center justify-center relative">
                  {template.preview_image ? (
                    <img
                      src={`data:image/png;base64,${template.preview_image}`}
                      alt={template.template_name}
                      className="w-full h-full object-contain"
                    />
                  ) : template.template_file && !template.template_filename?.endsWith('.pdf') ? (
                    <img
                      src={`data:image/png;base64,${template.template_file}`}
                      alt={template.template_name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-gray-400">
                      <FileText className="h-12 w-12" />
                      <p className="text-xs mt-1">PDF Template</p>
                    </div>
                  )}
                  <div 
                    className="absolute top-2 right-2 w-4 h-4 rounded-full border-2 border-white shadow"
                    style={{ backgroundColor: template.category_color }}
                    title={template.category_name}
                  />
                </div>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm truncate">{template.template_name}</p>
                      <p className="text-xs text-gray-500">{template.category_name}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-500 hover:text-blue-700"
                        onClick={() => handleOpenEditor(template)}
                        title="Edit Layout"
                      >
                        <Move className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteTemplate(template.template_id, template.template_name)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Default Template Info */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-sm mb-2">Default Badge Design</h4>
          <p className="text-sm text-gray-600">
            Categories without a custom template will use the default badge design, which includes:
          </p>
          <ul className="text-sm text-gray-600 list-disc list-inside mt-2 space-y-1">
            <li>Category-colored header with event name</li>
            <li>Attendee name (centered, bold)</li>
            <li>Category label</li>
            <li>Ticket ID</li>
            <li>Unique QR code for scanning</li>
          </ul>
        </div>
      </CardContent>

      {/* Badge Template Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-[95vw] w-[1200px] h-[90vh] p-0 flex flex-col overflow-hidden">
          {editingTemplate && (
            <BadgeTemplateEditor
              eventId={eventId}
              categoryId={editingTemplate.category_id}
              templateData={editingTemplate}
              onSave={handleEditorSave}
              onClose={() => setEditorOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BadgeTemplateManager;
